import { FC, useEffect, useState } from "react";
import { Box, PaletteMode, SxProps, Theme, useTheme } from "@mui/material";
import PlayerBox from "./referee-tablet-page/PlayerBox";
import { useAppDataContext } from "@context/AppContext";
import { capitalizeFirstLetter } from "@/utils/string";
import { Player } from "@/models/gameGeneral";
import { gameState, useTeamSides } from "@/services/gameState";
import { ConnectionStatus } from "@/models/ticker";
import { GameProgress } from "@/models/gameState";

interface TeamLineupProps {
  side: "left" | "right";
  fontSize: string;
  sx?: SxProps<Theme>;
}

const positionNumbers = ["I", "II", "III", "IV", "V", "VI"] as const;
export type Positions = (typeof positionNumbers)[number];

const gameFinalizedColors: { [key in PaletteMode]: string } = {
  light: "#46E446",
  dark: "#21ce21",
};

const connectionStatusColors: {
  [key in ConnectionStatus]: { [key in PaletteMode]: string };
} = {
  connected: {
    light: "#264df4",
    dark: "#1c3cc9",
  },
  loosing: {
    light: "#264df4",
    dark: "#1c3cc9",
  },
  lost: {
    light: "#ff0000",
    dark: "#ff0000",
  },
  paused: {
    light: "#4e5ea5",
    dark: "#3a51b5",
  },
};

const TeamLineup: FC<TeamLineupProps> = ({ side, sx, fontSize }) => {
  const { palette } = useTheme();

  const currentLineups = gameState("currentLineups");
  const teamSquads = gameState("teamSquads");
  const connectionStatus = gameState("connectionStatus");
  const gameProgress = gameState("gameProgress");
  const scoreSetup = gameState("scoreConfiguration");
  const { leftTeam, rightTeam } = useTeamSides(useAppDataContext());

  const expectedNumberOfPlayersOnField = Math.min(
    scoreSetup?.totalPlayersOnField || 6,
    6,
  );

  const lineWidth = "0.12em";
  const lineColor = (
    gameProgress >= GameProgress.FINALIZATION_SENT
      ? gameFinalizedColors
      : connectionStatusColors[connectionStatus]
  )[palette.mode];
  const spacing = "0.12em";

  const [teamReference, setTeamReference] = useState(
    side === "left" ? leftTeam : rightTeam,
  );
  const [positions, setPositions] =
    useState<{ [key in Positions]: Player | undefined }>();
  const [teamCaptainOnField, setTeamCaptainOnField] = useState(false);
  const [tooFewNonItcPlayersOnField, setTooFewNonItcPlayersOnField] =
    useState(false);

  useEffect(() => {
    setTeamReference(side === "left" ? leftTeam : rightTeam);
  }, [leftTeam, rightTeam, side]);

  useEffect(() => {
    if (teamReference && currentLineups?.[teamReference]) {
      setPositions({
        I: currentLineups[teamReference][0],
        II: currentLineups[teamReference][1],
        III: currentLineups[teamReference][2],
        IV: currentLineups[teamReference][3],
        V: currentLineups[teamReference][4],
        VI: currentLineups[teamReference][5],
      });
      setTeamCaptainOnField(
        Object.values(currentLineups?.[teamReference])
          .map((player: Player) => player?.uuid)
          .includes(teamSquads?.[teamReference]?.captain?.uuid || ""),
      );

      const itcPlayersOnField = Object.values(
        currentLineups?.[teamReference],
      ).filter(
        (player: Player | undefined) => player && !player.isNationalityGerman,
      ).length;
      const numberOfRequiredNonItcPlayersOnField =
        (scoreSetup?.itcRules?.active &&
          scoreSetup?.itcRules?.numberOfRequiredNonItcPlayersOnField) ||
        0;
      setTooFewNonItcPlayersOnField(
        expectedNumberOfPlayersOnField - itcPlayersOnField <
          numberOfRequiredNonItcPlayersOnField,
      );
    }
  }, [teamReference, currentLineups]);

  return (
    <Box
      display={"flex"}
      flexDirection={side === "left" ? "row" : "row-reverse"}
      justifyContent={"flex-end"}
      sx={sx}
    >
      <Box
        display={"grid"}
        gridTemplateAreas={
          side === "left"
            ? `
        "V    IV"
        "VI  III"
        "I    II"
        `
            : `
        "II    I"
        "III  VI"
        "IV    V" 
        `
        }
        gridTemplateColumns={"repeat(2, 1fr)"}
        gridTemplateRows={"repeat(3, 1fr)"}
        gap={spacing}
        p={spacing}
        borderTop={`${lineWidth} solid ${lineColor}`}
        borderBottom={`${lineWidth} solid ${lineColor}`}
        fontSize={fontSize}
        sx={{
          transition: "border 1s",
          [`border${capitalizeFirstLetter(side)}`]: `${lineWidth} solid ${lineColor}`,
        }}
        width={"100%"}
        maxWidth={"40vw"}
      >
        {positionNumbers.map((positionNumber) => (
          <PlayerBox
            key={positionNumber}
            position={positionNumber}
            player={positions?.[positionNumber]}
            team={teamReference}
            teamCaptainOnField={teamCaptainOnField}
            tooFewNonItcPlayersOnField={tooFewNonItcPlayersOnField}
          />
        ))}
      </Box>
    </Box>
  );
};

export default TeamLineup;
